export type HelpTopicId =
  | 'getting-started'
  | 'card-setup'
  | 'canvas'
  | 'visual-styles'
  | 'layers'
  | 'variables'
  | 'data-grid'
  | 'csv-import'
  | 'fonts'
  | 'render'
  | 'offline-install'
  | 'backup-recovery'

export interface HelpTopic {
  id: HelpTopicId
  category: 'Start here' | 'Design' | 'Data' | 'Printing' | 'Projects'
  title: string
  summary: string
  steps: string[]
  tip?: string
  searchTerms: string
}

export const HELP_TOPICS: HelpTopic[] = [
  {
    id: 'getting-started',
    category: 'Start here',
    title: 'How a Deckelier project works',
    summary: 'A project combines one visual template with a data row for every card in the deck.',
    steps: [
      'Design builds the shared card template.',
      'Data stores the content that changes from card to card.',
      'Render combines the template and data, checks the deck, and creates print files.',
      'Projects save automatically on this device. Save a backup when you want a portable copy.',
    ],
    tip: 'Start with a single card and one variable. Add more detail after the basic template and data flow works.',
    searchTerms: 'overview first deck design data render workflow',
  },
  {
    id: 'card-setup',
    category: 'Design',
    title: 'Card size, bleed, and safe area',
    summary: 'Set the finished size first, then use print guides to keep important content in the right place.',
    steps: [
      'The trim size is the final cut size of the card.',
      'Bleed extends artwork beyond the trim edge so cutting does not leave a white sliver.',
      'The safe area keeps text and important marks away from the cut edge.',
      'Square or curved guide corners only change the guide shape. They do not change the exported card size.',
    ],
    tip: 'Check the exact bleed and safe-area requirements from the company that will print the deck.',
    searchTerms: 'format dimensions trim size bleed safe area rounded corners border printer',
  },
  {
    id: 'canvas',
    category: 'Design',
    title: 'Canvas navigation and snapping',
    summary: 'Move around the canvas quickly and use snapping for deliberate placement and sizing.',
    steps: [
      'Scroll over the canvas to zoom.',
      'Drag the empty workspace, use the middle mouse button, or hold Space while dragging to pan.',
      'Snap aligns movement and resizing to the card, guides, and nearby elements.',
      'The 1 mm option adds grid snapping. Rotate snap controls angular steps separately.',
      'Position and size snapping pause for elements rotated away from a 90 degree angle.',
    ],
    searchTerms: 'zoom pan move resize snap grid rotate guides select canvas',
  },
  {
    id: 'visual-styles',
    category: 'Design',
    title: 'Shapes, images, and visual styles',
    summary: 'Closed shapes can hold colour, gradients, or project images and can use one outer shadow.',
    steps: [
      'Add a closed shape, then choose Solid, Gradient, or Image in its fill controls.',
      'Image fills stay inside the shape. Cover fills the area, Contain shows the full image, and Stretch matches the shape bounds.',
      'Use opacity, stroke, corners, rotation, and shadow controls to finish the layer.',
      'Lines, arcs, and rings have their own stroke and geometry controls.',
    ],
    tip: 'Upload an image once, then reuse it from the project image picker.',
    searchTerms: 'rectangle ellipse polygon star arc ring line image fill gradient shadow stroke opacity',
  },
  {
    id: 'layers',
    category: 'Design',
    title: 'Layers, grouping, and alignment',
    summary: 'The layer list controls stacking order and gives you a reliable way to find crowded elements.',
    steps: [
      'Drag a layer in the list to move it in front of or behind another layer.',
      'Hide layers while comparing alternatives and lock layers that should not move.',
      'Select several elements to align, distribute, group, duplicate, or move them together.',
      'Groups keep elements together without changing their coordinates or resizing behaviour.',
    ],
    searchTerms: 'layers order stack hide lock group ungroup align distribute duplicate hierarchy',
  },
  {
    id: 'variables',
    category: 'Data',
    title: 'Variables and property binding',
    summary: 'A bound property reads its value from the current card row instead of using one constant value.',
    steps: [
      'Select an element and find the property that should change between cards.',
      'Choose Bind beside that property.',
      'Select a compatible variable or choose Create new.',
      'Open Data and fill that variable for each card.',
      'Switch Preview data in Design to check different rows against the same template.',
    ],
    tip: 'Text, numbers, colours, yes or no values, and project images can all be stored in variables.',
    searchTerms: 'variable bind binding property constant dynamic per card create column',
  },
  {
    id: 'data-grid',
    category: 'Data',
    title: 'Editing the data grid',
    summary: 'The grid separates cell selection from cell editing, similar to a desktop spreadsheet.',
    steps: [
      'Click a cell to select it. Drag or hold Shift to select a range.',
      'Double-click, press F2, or start typing to edit a cell.',
      'Enter commits an edit. Escape cancels it.',
      'Copy and paste rectangular ranges, fill repeated values, or create number sequences.',
      'Use the final column header to add a new variable column.',
    ],
    tip: 'While a cell is being edited, Home, End, and text shortcuts work inside the field.',
    searchTerms: 'spreadsheet cells rows columns edit mode selection copy paste fill sequence keyboard image',
  },
  {
    id: 'csv-import',
    category: 'Data',
    title: 'Importing a large deck',
    summary: 'CSV and TSV import can add rows, match existing columns, or create new variables in one review step.',
    steps: [
      'Open Data and choose Import CSV.',
      'Confirm the delimiter and whether the first row contains headings.',
      'Match each source column to the card name, an existing variable, a new variable, or Ignore.',
      'Review the preview and import mode before committing the change.',
      'Keep a backup before replacing a large finished data set.',
    ],
    searchTerms: 'csv tsv import spreadsheet bulk columns mapping replace append',
  },
  {
    id: 'fonts',
    category: 'Design',
    title: 'Using your own fonts',
    summary: 'Project fonts are stored with the deck and are used by previews and exported files.',
    steps: [
      'Select a text layer and open Manage fonts from the typography controls.',
      'Add supported font files and assign the correct weight and style to each face.',
      'Choose the project font on a text layer.',
      'Check several cards in Render before exporting, especially when text is tightly fitted.',
    ],
    tip: 'Only use fonts whose licence permits the way you plan to distribute or print the deck.',
    searchTerms: 'font upload typography typeface weight italic family licence export',
  },
  {
    id: 'render',
    category: 'Printing',
    title: 'Preflight, preview, and export',
    summary: 'Render checks the full deck and shows the same card layout used by PNG and PDF exports.',
    steps: [
      'Choose a production profile, resolution, paper size, margins, and card gap.',
      'Review the imposed sheets and individual card previews.',
      'Resolve blocking preflight errors and inspect warnings.',
      'Download a sample PNG before exporting the whole deck.',
      'Use Card image ZIP for printer-ready card files or Print-sheet PDF for arranged pages.',
    ],
    tip: 'A physical test print is the best way to check text size, contrast, and trimming before a full order.',
    searchTerms: 'render print preflight png pdf zip dpi professional home sheet crop marks export',
  },
  {
    id: 'offline-install',
    category: 'Projects',
    title: 'Installing Deckelier and working offline',
    summary: 'The desktop app keeps the complete editor available without a connection.',
    steps: [
      'On a supported desktop, choose Install Deckelier from the project library.',
      'If your browser does not show an installer, use its app installation command. In Safari, choose File, then Add to Dock.',
      'After the offline files are prepared, Design, Data, Render, and exports work without a connection.',
      'When an update is ready, Deckelier asks before saving your work and reloading.',
      'Installation does not sync projects. Save portable backups for important work.',
    ],
    tip: 'The editor and installation require a desktop-sized screen with a mouse or trackpad.',
    searchTerms: 'install app pwa desktop offline no connection update service worker dock',
  },
  {
    id: 'backup-recovery',
    category: 'Projects',
    title: 'Saving, backups, and recovery',
    summary: 'Work saves locally, while project files provide a portable backup you control.',
    steps: [
      'Deckelier autosaves changes in this browser on this device.',
      'Choose Save backup to download a self-contained .deckelier file.',
      'Import that file from the project library to open it on another device or browser.',
      'Storage & recovery shows browser storage status and available recovery copies.',
      'Keep important backups outside the browser, especially before clearing site data.',
    ],
    searchTerms: 'save autosave backup project file import storage recovery local device browser',
  },
]

export const helpTopic = (id: HelpTopicId) => HELP_TOPICS.find((topic) => topic.id === id) ?? HELP_TOPICS[0]

export const filterHelpTopics = (query: string) => {
  const normalized = query.trim().toLocaleLowerCase()
  if (!normalized) return HELP_TOPICS
  return HELP_TOPICS.filter((topic) => [topic.title, topic.summary, topic.category, topic.searchTerms, ...topic.steps]
    .join(' ')
    .toLocaleLowerCase()
    .includes(normalized))
}
